<template>
    <div>

        <ComponentDialog v-if="showComponentDialog" v-bind="componentDialogParameters"
                         v-on:hidden="showComponentDialog = false"
                         v-on:clear-selection="clearSelection"
                         v-on:click-row="setInstancePath"
                         v-on:select-component="zoomComponent"
                         v-on:zoom-component="zoomExtentsComponent"
                         v-on:zoom-reset="zoomReset"/>

        <div class="card st-framework-card">

            <div class="card-header">
                <table style="width: 100%;">
                    <tr>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-if="environment === 'Local'">
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPluginFiles(['punch_plates/punch_plates.rb'])">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-else>
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPage()">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0; width: 230px;">
                            {{ $i18n.tc('translations.' + name, 2).toUpperCase() }}
                        </td>
                        <template v-if="serverDataLoaded">
                            <td style="width: 1rem;"></td>
                            <template v-if="data.model.project">
                                <td style="padding: 0 10px; border: solid 1px lightgray; width: 30%;" v-if="data.model.phase">
                                    {{ data.model.phase.label }}
                                </td>
                                <td style="width: 30%;" v-else/>
                                <td style="width: 1rem;"/>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <LinkAction :action="actions.modelLinkAction"/>
                                </td>
                                <td style="padding: 0; width: 90px; text-align: right;">
                                    (#{{ data.model.phase ? data.model.phase.id : (data.model.order ? data.model.order.id : data.model.project.id) }})
                                </td>
                            </template>
                            <template v-else>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <router-link :to="{name: 'order_list'}" class="st-cardheader-link">
                                        {{ $i18n.t('translations.SketchUp model is not linked to a project') }}!
                                    </router-link>
                                </td>
                            </template>
                            <td style="width: 1rem;"></td>
                        </template>
                        <td v-else/>
                    </tr>
                </table>
            </div>

            <template v-if="incompatibleSketchUpPlugin">
                <div class="card-body" style="text-align: center; font-size: larger; font-weight: bold; color: red;">
                    <div>{{ $i18n.t("translations.incompatibleSketchUpPluginVersionForModule", {version: this.minimumSketchUpPluginVersion}) }}</div>
                </div>
            </template>
            <template v-else-if="serverDataLoaded">
                <div class="card-body p-0">

                    <div class="card" style="border: none;">
                        <div class="card-header light p-0" style="border: none;">
                            <table style="width: 100%;">
                                <tr>
                                    <td>
                                        <template v-for="(action,actionIndex) in actions.document">
                                            <template v-if="action.placement === 'DOCUMENTHEADERLEFT'">
                                                <ToggleAction :key="action.name+'-'+actionIndex"
                                                              :action="action" :disabled="state.disabled"
                                                              v-on:toggle-auto-zoom="toggleAutoZoom"
                                                              v-on:toggle-viewer="toggleViewer"
                                                              v-if="action.type === 'TOGGLE'"/>
                                            </template>
                                        </template>
                                    </td>
                                    <td>
                                        <div class="float-right">
                                            <template v-for="(action,actionIndex) in actions.document">
                                                <template v-if="action.placement === 'DOCUMENTHEADERRIGHT'">
                                                    <ButtonAction :key="action.name+'-'+actionIndex"
                                                                  :action="action" :disabled="state.disabled"
                                                                  v-on:generate-dxf-files="launchPunchPlates"
                                                                  v-if="action.type === 'BUTTON'"/>
                                                </template>
                                            </template>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <div class="view">
                        <div class="view-picture" v-if="previewMode">
                            <ViewComponent :key="'preview-'+reloadPreviewComponent"
                                           :width="punchPlateViewerWidth" :height="punchPlateViewerHeight"
                                           :component="previewComponent" :vertex-threshold="vertexThreshold"/>
                        </div>
                        <div class="view-table">
                            <Table :key="'component-table-'+reloadTableComponent" card-class="mt-0 mb-0" header-class="light" footer-class="light" body-class=""
                                   with-filter with-pagination pagination-always-visible no-state-loading autofocus
                                   highlight-rows-on-click :highlight-row-id="getHighlightedRowId"
                                   :lines-per-page=data.crudLinesPerPage th-actions-style="width: 155px;"
                                   :reload-table-data="reloadTableData"
                                   :rows="processedComponentList"
                                   :fields="fields.table" :actions="actions.table" :state="state"
                                   :total-fields="fields.tableTotals" :totals="processedComponentTotals"
                                   v-on:clear-selection="clearSelection"
                                   v-on:delete-selection="deleteSelection"
                                   v-on:detail-components="detailComponents"
                                   v-on:export-event="exportPunchPlates"
                                   v-on:highlight-row="highlightRow"
                                   v-on:ignore-flipped-for-unfolded-plates="ignoreFlippedForUnfoldedPlates"
                                   v-on:list-event="listPunchPlates"
                                   v-on:make-unique-by-id="makeUniqueById"
                                   v-on:refresh="getPunchPlates"
                                   v-on:reset-ignore-flipped="resetIgnoreFlipped"
                                   v-on:select-all="selectAll"
                                   v-on:select-component="selectComponent"
                                   v-on:select-face-material="selectFaceMaterial"
                                   v-on:select-hide-definitions="selectHideDefinitions"
                                   v-on:set-ignore-flipped="setIgnoreFlipped"
                                   v-on:toggle-bend-allowance-deduction="toggleBendAllowanceDeduction"
                                   v-on:toggle-face-material-choices="toggleFaceMaterialChoices"
                                   v-on:zoom-component="zoomExtentsComponent"
                                   v-on:zoom-reset="zoomReset"/>
                        </div>
                    </div>

                </div>
            </template>

        </div>
    </div>
</template>

<script>
import ButtonAction from "@/components/actions/ButtonAction.vue";
import ComponentDialog from "@/components/dialogs/sketchup/ComponentDialog.vue";
import FileDownload from "@/mixins/FileDownload";
import LinkAction from "@/components/actions/LinkAction.vue";
import SketchUp from "@/mixins/SketchUp";
import Table from "@/components/Table";
import ToggleAction from "@/components/actions/ToggleAction.vue";
import ViewComponent from "@/components/sketchup/ViewComponent_v1_27_0.vue";

/* global sketchup:false */

export default {
    name: 'PunchPlates_v1_27_0',
    components: {
        ButtonAction,
        ComponentDialog,
        LinkAction,
        Table,
        ToggleAction,
        ViewComponent,
    },
    props: ['name', 'icon', 'api', 'state', 'environment'],
    mixins: [FileDownload, SketchUp],
    data() {
        return {
            serverDataLoaded: false,
            reloadTableComponent: 0,
            reloadTableData: 0,
            sketchUpToolData: null,
            minimumSketchUpPluginVersion: '1.26.0',
            incompatibleSketchUpPlugin: false,
            data: null,
            fields: null,
            actions: null,
            reloadPreviewComponent: 0,
            autoZoom: false,
            previewMode: true,
            previewComponent: null,
            punchPlateViewerWidth: 640,
            punchPlateViewerHeight: 480,
            vertexThreshold: 10,
            rawComponentList: [],
            processedComponentList: [],
            processedComponentTotals: {},
            enableBendAllowanceDeduction: true,
            hideDefinitions: [],
            faceMaterialChoices: [],
            faceMaterialChoicesFiltered: true,
            showComponentDialog: false,
            componentDialogParameters: {
                title: null,
                actions: null,
                fields: null,
                rows: null,
            },
        }
    },
    computed: {
        getHighlightedRowId: function () {
            if (this.processedComponentList.length > 0) {
                if (this.previewComponent) {
                    let idx = this.processedComponentList.findIndex(component => component.definition === this.previewComponent.definition);
                    if (idx !== -1) {
                        return this.processedComponentList[idx].id;
                    }
                }
                return this.processedComponentList[0].id;
            }
            return null;
        },
    },
    created() {
        window.vm.Tool = this;
        this.getToolData();
    },
    methods: {
        clearSelection() {
            sketchup.clearSelection();
        },
        deleteSelection(data) {
            data.ids.forEach(id => {
                let idx = this.processedComponentList.findIndex(component => component.id === id);
                this.processedComponentList.splice(idx, 1);
            });
            this.reloadTableData++;
        },
        detailComponents(component) {
            let rows = [];
            component.instances.forEach(instance => {
                let definition_path = '';
                instance.definition_path.forEach((d, i) => {
                    definition_path += (definition_path ? ' > ' : '') + d + ' (' + instance.instance_path[i] + ')';
                });
                rows.push({
                    id: rows.length + 1,
                    entity_id: instance.entity_id,
                    instances: [{
                        instance_path: instance.instance_path,
                    }],
                    definition_path: definition_path,
                    flipped: instance.is_flipped ? 'X' : '',
                });
            });
            this.componentDialogParameters.title = component.definition;
            this.componentDialogParameters.actions = this.actions.detail;
            this.componentDialogParameters.fields = this.fields.detail;
            this.componentDialogParameters.rows = rows;
            this.showComponentDialog = true;
        },
        exportPunchPlates(choice) {
            this.state.loading = true;
            let components = [];
            this.processedComponentList.forEach(component => {
                if (!choice?.ids?.length || choice.ids.includes(component.id)) {
                    components.push({
                        definition: component.definition,
                        components_in_selection: component.components_in_selection,
                        instances_in_selection: component.instances_in_selection,
                        cnt_folds: component.cnt_folds,
                        angles: component.angles,
                        bend_allowance_deduction: component.bend_allowance_deduction,
                        thickness: component.thickness,
                        area_square_m: component.area_square_m,
                        weight: component.weight,
                        cnt_faces: component.cnt_faces,
                        circles: component.circles,
                        orientation_errors: component.orientation_errors,
                        flipped: component.flipped,
                        dimensions: component.dimensions ? component.dimensions.join('x').replaceAll('.', ',') : '',
                    });
                }
            });
            this.$http.post(choice.api, {
                'components': components,
            }, {'responseType': 'blob'}).then((res) => {
                this.fileDownload(choice, res.data);
                this.state.loading = false;
            }).catch((error) => {
                console.log("PunchPlates:exportPunchPlates():error:", error);
            });
        },
        fetchData() {
            this.$http.get(this.api + '/manage_punchplates_v1_27_0/' + this.sketchUpToolData.project_id + '/' + this.sketchUpToolData.order_id + '/' + this.sketchUpToolData.phase_id, {}).then((res) => {
                this.actions = res.data.actions;
                this.fields = res.data.fields;
                this.data = res.data.data;
                this.filterTableActionsAndFields();
                this.previewMode = !!parseInt(this.data.previewMode);
                this.autoZoom = !!parseInt(this.data.autoZoom);
                this.punchPlateViewerWidth = parseInt(this.data.punchPlateViewerWidth);
                this.punchPlateViewerHeight = this.punchPlateViewerWidth * 3 / 4;
                this.setFaceMaterialChoices();
                this.serverDataLoaded = true;
                this.state.loading = false;
            }).catch((error) => {
                console.log("PunchPlates:fetchData():error:", error);
            });
        },
        filterTableActionsAndFields() {
            if (this.actions) {
                this.actions.table.filter(action => action.hideintable = this.previewMode && !!action.hideinpreview);
            }
            if (this.fields) {
                this.fields.table.filter(field => field.hideintable = this.previewMode && !!field.hideinpreview);
                this.fields.tableTotals.filter(field => field.hideintable = this.previewMode && !!field.hideinpreview);
            }
        },
        getPunchPlates() {
            let materials = [];
            this.faceMaterialChoices.forEach(choice => {
                if (choice.value === true) {
                    materials.push({
                        color_code: choice.color_code,
                        display_name: choice.label,
                    });
                }
            });
            if (materials.length === 0) {
                return;
            }
            if (!this.isVersionGreaterOrEqual(this.sketchUpToolData.plugin_version, '1.27.0')) {
                materials = materials[0];
            }

            this.state.loading = true;
            sketchup.getPunchPlates(materials, this.enableBendAllowanceDeduction ? this.data.bendAllowanceDeduction : []);
        },
        getPunchPlatesCallback(entities) {
            this.rawComponentList = JSON.parse(entities);
            this.processComponentList();
        },
        getToolData() {
            this.state.loading = true;
            sketchup.getToolData();
        },
        getToolDataCallback(data) {
            this.sketchUpToolData = data;
            if (this.isVersionGreaterOrEqual(this.sketchUpToolData.plugin_version, this.minimumSketchUpPluginVersion)) {
                this.fetchData();
            } else {
                this.incompatibleSketchUpPlugin = true;
                this.state.loading = false;
            }
        },
        highlightRow(component) {
            this.previewComponent = component;
            if (this.autoZoom) {
                this.zoomExtentsComponent(this.previewComponent);
            }
            this.reloadPreviewComponent++;
        },
        ignoreFlippedForUnfoldedPlates() {
            this.state.loading = true;
            let definitions = [];
            this.rawComponentList.forEach(component => {
                if (component.folds.length === 0 && !component.ignore_flipped) {
                    component.ignore_flipped = true;
                    definitions.push(component.definition);
                }
            });
            sketchup.setDefinitionAttributes(definitions, {ignore_flipped: true});
            this.processComponentList();
        },
        launchPunchPlates(data) {
            this.state.loading = true;

            // XML Document
            let xml = document.implementation.createDocument(null, "PunchPlates");
            let elements = xml.getElementsByTagName("PunchPlates");

            // Options element
            let options = xml.createElement("Options");
            options.setAttribute("version", this.data.punchPlateUtilityVersion);
            if ('polygon-offset' in data) {
                options.setAttribute("polygonOffset", data['polygon-offset']);
            } else {
                options.setAttribute("polygonOffset", '0');
            }
            if ('filename-prefix' in data) {
                options.setAttribute("fileNamePrefix", data['filename-prefix']);
            } else {
                options.setAttribute("fileNamePrefix", '');
            }
            if ('label-prefix' in data) {
                options.setAttribute("labelPrefix", data['label-prefix']);
            } else {
                options.setAttribute("labelPrefix", '');
            }
            options.setAttribute("targetDir", this.data.dropBoxLocation);
            elements[0].appendChild(options);

            let noRowsSelected = true;
            this.processedComponentList.forEach(punchplate => {
                if (punchplate.selected) {
                    noRowsSelected = false;
                }
            });
            this.processedComponentList.forEach(p => {
                if (noRowsSelected || p.selected) {
                    let punchplate = xml.createElement("PunchPlate");
                    punchplate.setAttribute("definition", p.definition);
                    let prefix = data['label-prefix'] ? data['label-prefix'] : p.definition.substring(0, 1);
                    punchplate.setAttribute("label", prefix + this.getDefinitionNumber(p.definition));
                    punchplate.setAttribute("thickness", p.thickness);
                    punchplate.setAttribute("quantity", p.instances_in_selection);
                    if (p.fileNameSuffix) {
                        punchplate.setAttribute("fileNameSuffix", p.fileNameSuffix);
                    }
                    let face = xml.createElement("Face");

                    p.loops.forEach(l => {
                        let loop = xml.createElement("Loop");
                        loop.setAttribute("outer", l.outer);
                        loop.setAttribute("circle", l.circle);
                        if (l.circle) {
                            let arccurve = xml.createElement("ArcCurve");
                            arccurve.setAttribute("x", l.arccurve.x);
                            arccurve.setAttribute("y", l.arccurve.y);
                            arccurve.setAttribute("radius", l.arccurve.radius);
                            loop.appendChild(arccurve);
                        } else {
                            l.vertices.forEach(v => {
                                let vertex = xml.createElement("Vertex");
                                vertex.setAttribute("x", v.x);
                                vertex.setAttribute("y", v.y);
                                loop.appendChild(vertex);
                            });
                        }
                        face.appendChild(loop);
                    });

                    p.folds.forEach(f => {
                        f.markers.forEach(m => {
                            let fold = xml.createElement("Fold");
                            fold.setAttribute("x1", m.x1);
                            fold.setAttribute("y1", m.y1);
                            fold.setAttribute("x2", m.x2);
                            fold.setAttribute("y2", m.y2);
                            face.appendChild(fold);
                        })
                    });

                    punchplate.appendChild(face);
                    elements[0].appendChild(punchplate);
                }
            });

            let sXml = (new XMLSerializer()).serializeToString(xml);
            sketchup.launchPunchPlates(process.env.VUE_APP_TEMP_DIR, process.env.VUE_APP_PUNCHPLATES_DIR, sXml);
        },
        launchPunchPlatesCallback() {
            this.state.loading = false;
        },
        listPunchPlates(choice) {
            this.state.loading = true;
            let punch_plates = [];
            this.processedComponentList.forEach(component => {
                if (!choice?.ids?.length || choice.ids.includes(component.id)) {
                    punch_plates.push({
                        definition: component.definition,
                        quantity: component.instances_in_selection,
                        thickness: component.thickness,
                        area_square_m: component.area_square_m,
                        weight: component.weight,
                    });
                }
            });
            this.$http.post(choice.api + '/' + this.sketchUpToolData.order_id, {
                'punch_plates': punch_plates,
            }, {'responseType': 'blob'}).then((res) => {
                this.fileDownload(choice, res.data);
                this.state.loading = false;
            }).catch((error) => {
                console.log("PunchPlates:listPunchPlates():error:", error);
            });
        },
        // makeUnique(component) {
        //     this.state.loading = true;
        //     let idx = this.processedComponentList.findIndex(item =>
        //         (item.definition === component.definition) &&
        //         (item.is_flipped !== component.is_flipped)
        //     );
        //
        //     let instancePaths = component.instances.map(i => i.instance_path);
        //     let flippedInstancePaths = this.processedComponentList[idx].instances.map(i => i.instance_path);
        //
        //     sketchup.makeUnique(instancePaths, flippedInstancePaths);
        // },
        // makeUniqueCallback() {
        //     this.getPunchPlates();
        // },
        makeUniqueById(component) {
            this.state.loading = true;
            let ids = component.instances.map(i => i.entity_id);
            sketchup.makeUniqueById(ids);
        },
        makeUniqueByIdCallback() {
            this.getPunchPlates();
        },
        processComponentList() {
            this.$worker.run((rawComponentList, enableBendAllowanceDeduction, vertexThreshold, hideDefinitions) => {
                let newProcessedComponentList = [];
                let newProcessedComponentTotals = {
                    instances_in_selection: 0,
                    area_square_m: 0,
                    weight: 0,
                    circles: '',
                };
                let circleTotals = [];

                rawComponentList.forEach(component => {
                    let idx = component.definition.lastIndexOf('#');
                    let definition = idx === -1 ? component.definition : component.definition.substring(0, idx + 1);
                    if (hideDefinitions.includes(definition)) {
                        return;
                    }

                    idx = newProcessedComponentList.findIndex(item => item.definition === component.definition);
                    if (component.ignore_flipped && idx > -1) {
                        newProcessedComponentList[idx].instances = newProcessedComponentList[idx].instances.concat(component.instances);
                        let entityIds = newProcessedComponentList[idx].instances.map(i => i.entity_id);
                        newProcessedComponentList[idx].components_in_selection = new Set(entityIds).size;
                        newProcessedComponentList[idx].instances_in_selection = newProcessedComponentList[idx].instances.length;
                    } else {
                        let newComponent = component;
                        newComponent.id = newProcessedComponentList.length + 1;
                        newComponent.selected = false;
                        let entityIds = newComponent.instances.map(i => i.entity_id);
                        newComponent.components_in_selection = new Set(entityIds).size;
                        newComponent.instances_in_selection = newComponent.instances.length;
                        newComponent.cnt_faces = newComponent.face_ids.length;
                        newComponent.cnt_folds = newComponent.folds.length;
                        newComponent.cnt_folds_f = newComponent.cnt_folds > 0 ? newComponent.cnt_folds : '';
                        newComponent.thickness_f = newComponent.thickness ? newComponent.thickness.toFixed(1).replace('.', ',') : '';
                        newComponent.orientation_errors_f = newComponent.orientation_errors > 0 ? newComponent.orientation_errors : '';
                        newComponent.fileNameSuffix = null;
                        newComponent.color = {
                            value: component?.filter_material?.color_code,
                            label: component?.filter_material?.display_name,
                        };

                        newComponent.hide_set_ignore_flipped = true;
                        newComponent.hide_reset_ignore_flipped = !newComponent.ignore_flipped;
                        newComponent.hide_make_unique_by_id = true;
                        if (newComponent.ignore_flipped) {
                            newComponent.flipped = '!';
                            newComponent.flipped_style = "background: orange; color: white;";
                        } else {
                            newComponent.flipped = newComponent.is_flipped ? 'X' : '';
                        }

                        let circles = [];
                        newComponent.loops.forEach(loop => {
                            if (loop.circle) {
                                let diameter = Math.round(loop.arccurve.radius * 200) / 100;
                                let i = circles.findIndex(circle => circle.diameter === diameter);
                                if (i === -1) {
                                    circles.push({
                                        'diameter': diameter,
                                        'count': 1,
                                    })
                                } else {
                                    circles[i].count++;
                                }
                            }
                            if (!loop.circle && !loop.outer) {
                                if (loop.vertices.length >= vertexThreshold) {
                                    newComponent.id_style = newComponent.id_style ? newComponent.id_style : "background: orange; color: white;";
                                    newComponent.circles_style = "background: orange; color: white;";
                                }
                            }
                        });
                        circles.sort((a, b) => a.diameter - b.diameter);
                        newComponent.circles = '';
                        circles.forEach(circle => {
                            let i = circleTotals.findIndex(t => t.diameter === circle.diameter);
                            let count = newComponent.instances_in_selection * circle.count;
                            if (i === -1) {
                                circleTotals.push({
                                    'diameter': circle.diameter,
                                    'count': count,
                                })
                            } else {
                                circleTotals[i].count += count;
                            }
                            let c = circle.count + ' x ' + circle.diameter.toString().replace('.', ',') + 'mm';
                            newComponent.circles += (newComponent.circles ? ', ' + c : c);
                        });

                        newComponent.angles = '';
                        newComponent.bend_allowance_deduction = '';
                        newComponent.folds.forEach(fold => {
                            if (fold.concave === null) {
                                newComponent.id_style = newComponent.id_style ? newComponent.id_style : "background: orange; color: white;";
                                newComponent.folds_style = "background: orange; color: white;";
                            }
                            newComponent.angles += (newComponent.angles ? ', ' : '') + fold.angle + '°';
                            if (enableBendAllowanceDeduction) {
                                newComponent.bend_allowance_deduction += (newComponent.bend_allowance_deduction ? ', ' : '') + (fold.allowance_deduction !== null ? fold.allowance_deduction.toString() : 'n/a');
                            }
                        });

                        if (newComponent.fold_errors) {
                            newComponent.id_style = "background: red; color: white;";
                            newComponent.folds_style = "background: red; color: white;";
                        }

                        if (newComponent.orientation_errors > 0) {
                            newComponent.id_style = "background: red; color: white;";
                            newComponent.orientation_errors_style = "background: red; color: white;";
                        }

                        // check if the component also exist with opposite flipped status, if this is the case mark the flipped component as a possible problem!
                        let j = newProcessedComponentList.findIndex(item =>
                            (item.definition === newComponent.definition) &&
                            (item.is_flipped !== newComponent.is_flipped)
                        );
                        if (j > -1) {
                            newComponent.hide_make_unique_by_id = false;
                            newComponent.hide_set_ignore_flipped = false;
                            newProcessedComponentList[j].hide_make_unique_by_id = false;
                            newProcessedComponentList[j].hide_set_ignore_flipped = false;

                            if (newComponent.is_flipped) {
                                newComponent.id_style = "background: red; color: white;";
                                newComponent.definition_style = 'background: red; color: white;';
                                newComponent.fileNameSuffix = "__FLIPPED";
                            } else {
                                newProcessedComponentList[j].id_style = "background: red; color: white;";
                                newProcessedComponentList[j].definition_style = 'background: red; color: white;';
                                newProcessedComponentList[j].fileNameSuffix = "__FLIPPED";
                            }
                        }

                        if (newComponent.fatal_error) {
                            newComponent.id_style = "background: red; color: white;";
                        }

                        newProcessedComponentList.push(newComponent);
                    }
                    newProcessedComponentTotals.instances_in_selection += component.instances.length;
                });

                newProcessedComponentList.forEach(component => {
                    let area_square_m = component.instances_in_selection * (component.area ? component.area : 0) / 1000000;
                    component.area_square_m = area_square_m;
                    component.area_square_m_f = area_square_m.toFixed(2).replace('.', ',');
                    newProcessedComponentTotals.area_square_m += area_square_m;
                    let weight = Math.round(100 * 7850 * area_square_m * (component.thickness ? component.thickness : 0) / 1000) / 100;
                    component.weight = weight;
                    component.weight_f = weight ? weight.toFixed(2).replace('.', ',') : '';
                    newProcessedComponentTotals.weight += weight;

                    // make unique button will not work properly if entity_id's are shared between lines
                    if (!component.hide_make_unique_by_id) {
                        let i = newProcessedComponentList.findIndex(item =>
                            (item.definition === component.definition) &&
                            (item.is_flipped !== component.is_flipped)
                        );
                        let hide_make_unique_by_id = newProcessedComponentList[i].instances.some(c1 => component.instances.findIndex(c2 => c2.entity_id === c1.entity_id) > -1);
                        newProcessedComponentList[i].hide_make_unique_by_id = hide_make_unique_by_id;
                        component.hide_make_unique_by_id = hide_make_unique_by_id;
                    }
                });
                newProcessedComponentTotals.area_square_m_f = newProcessedComponentTotals.area_square_m.toFixed(2).replace('.', ',');
                newProcessedComponentTotals.weight_f = newProcessedComponentTotals.weight.toFixed(2).replace('.', ',');

                newProcessedComponentList.sort(function (a, b) {
                    let idxA = a.definition.lastIndexOf('#');
                    let idxB = b.definition.lastIndexOf('#');
                    if (idxA > -1 &&
                        idxB > -1 &&
                        a.definition.substring(0, idxA) === b.definition.substring(0, idxB) &&
                        !isNaN(parseInt(a.definition.substring(idxA + 1))) &&
                        !isNaN(parseInt(b.definition.substring(idxB + 1)))) {
                        if (parseInt(a.definition.substring(idxA + 1)) < parseInt(b.definition.substring(idxB + 1))) return -1;
                        if (parseInt(a.definition.substring(idxA + 1)) > parseInt(b.definition.substring(idxB + 1))) return 1;
                    } else {
                        if (a.definition < b.definition) return -1;
                        if (a.definition > b.definition) return 1;
                    }
                    if (a.is_flipped < b.is_flipped) return -1;
                    if (a.is_flipped > b.is_flipped) return 1;
                    return 0;
                });

                circleTotals.sort((a, b) => a.diameter - b.diameter);
                circleTotals.forEach(circle => {
                    let c = circle.count + ' x ' + circle.diameter.toString().replace('.', ',') + 'mm';
                    newProcessedComponentTotals.circles += (newProcessedComponentTotals.circles ? ', ' + c : c);
                });

                return [
                    newProcessedComponentList,
                    newProcessedComponentTotals
                ];
            }, [this.rawComponentList, this.enableBendAllowanceDeduction, this.vertexThreshold, this.hideDefinitions]).then(data => {
                this.processedComponentList = data[0];
                this.processedComponentTotals = data[1];
                this.reloadTableData++;
                this.state.loading = false;
            }).catch(error => {
                console.log("PunchPlates:processComponentList():error:", error);
            });
        },
        renderingOptions(show) {
            sketchup.renderingOption('InactiveHidden', show);
            sketchup.renderingOption('InstanceHidden', show);
        },
        resetIgnoreFlipped(component) {
            this.state.loading = true;
            sketchup.setDefinitionAttributes([component.definition], {ignore_flipped: false});
            this.rawComponentList.forEach(c => {
                if (c.definition === component.definition) {
                    c.ignore_flipped = false;
                }
            });
            this.processComponentList();
        },
        selectAll(data) {
            let ids = [];
            this.processedComponentList.forEach(component => {
                if (data.ids.includes(component.id)) {
                    component.instances.forEach(instance => {
                        ids.push(instance.entity_id);
                    });
                }
            });
            sketchup.selectComponents(ids);
        },
        selectComponent(component) {
            let ids = component.instances.map(i => i.entity_id);
            sketchup.selectComponents(ids);
        },
        selectFaceMaterial(choices) {
            this.faceMaterialChoices = choices;
        },
        selectHideDefinitions(choices) {
            this.hideDefinitions = choices.filter(choice => choice.value === true).map(choice => choice.label);
        },
        setFaceMaterialChoices() {
            let materialChoices = [];
            this.sketchUpToolData.materials.forEach(material => {
                if (!this.faceMaterialChoicesFiltered || material.display_name.toLowerCase().includes('mm')) {
                    if (materialChoices.findIndex(choice => choice.label === material.display_name && choice.color_code + material.color_code) === -1) {
                        materialChoices.push({
                            'icon': ['fas', 'circle'],
                            'iconsize': 'xs',
                            'iconstyle': 'color:' + material.color_code,
                            'label': material.display_name,
                            'color_code': material.color_code,
                            'value': this.faceMaterialChoices.some(choice => choice.value === true && choice.color_code === material.color_code && choice.label === material.display_name),
                        });
                    }
                }
            });
            materialChoices.sort((a, b) => (a.label > b.label) ? 1 : ((a.label < b.label) ? -1 : 0));
            this.faceMaterialChoices = materialChoices;

            let actionIdx = this.actions.table.findIndex(action => action.name === 'select-face-material');
            this.actions.table[actionIdx].choices = materialChoices;

            this.reloadTableComponent++;
        },
        setIgnoreFlipped(component) {
            this.state.loading = true;
            sketchup.setDefinitionAttributes([component.definition], {ignore_flipped: true});
            this.rawComponentList.forEach(c => {
                if (c.definition === component.definition) {
                    c.ignore_flipped = true;
                }
            });
            this.processComponentList();
        },
        setInstancePath(component) {
            sketchup.setInstancePath(component.instances[0].instance_path);
        },
        toggleAutoZoom(action) {
            this.autoZoom = action.toggled;
            if (this.autoZoom) {
                this.zoomExtentsComponent(this.previewComponent);
            }
        },
        toggleBendAllowanceDeduction(action) {
            this.enableBendAllowanceDeduction = action.toggled;
        },
        toggleFaceMaterialChoices(action) {
            this.faceMaterialChoicesFiltered = action.toggled
            this.setFaceMaterialChoices();
        },
        toggleViewer(action) {
            this.previewMode = action.toggled;
            this.filterTableActionsAndFields();
            this.reloadTableComponent++;
        },
        zoomComponent(component) {
            sketchup.zoomByInstancePath(component.instances[0].instance_path);
        },
        zoomExtentsComponent(component) {
            if (component) {
                this.renderingOptions(true);
                sketchup.zoomExtentsByInstancePath(component.instances[0].instance_path);
            }
        },
        zoomReset() {
            this.renderingOptions(false);
            sketchup.zoomExtents();
        },
    },
}
</script>

<style scoped>
.view {
    display: flex;
    margin: 0 0 1rem 0;
}

.view-picture {
    display: inline-block;
    margin: 0 0 0 1rem;
}

.view-table {
    width: 100%;
    display: inline-block;
}
</style>